import { useState, useCallback, useEffect } from 'react';
import {
  X,
  Edit2,
  Trash2,
  Clock,
  Repeat,
  ListChecks,
  FileText,
  CheckSquare,
  Plus,
  Check,
  Circle,
  CheckCircle,
  Monitor
} from 'lucide-react';
import AgendaItem from './AgendaItem';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function formatMeetingDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatTime(time) {
  if (!time) return '';
  return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export default function MeetingDetailView({
  meeting,
  onClose,
  onEdit,
  onComplete,
  onUpdate,
  onDelete,
  onEnterMeetingMode,
  series,
  documents = [],
  policies = []
}) {
  const [notes, setNotes] = useState(meeting.notes || '');
  const [newActionItem, setNewActionItem] = useState('');
  const [agendaItems, setAgendaItems] = useState(meeting.agendaItems || []);
  const [actionItems, setActionItems] = useState(meeting.actionItems || []);
  const [notesDebounce, setNotesDebounce] = useState(null);
  const [expandedAgendaItems, setExpandedAgendaItems] = useState(new Set());
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragTargetIndex, setDragTargetIndex] = useState(null);

  // Sync state when meeting changes
  useEffect(() => {
    setNotes(meeting.notes || '');
    setAgendaItems(meeting.agendaItems || []);
    setActionItems(meeting.actionItems || []);
  }, [meeting.id, meeting.notes, meeting.agendaItems, meeting.actionItems]);

  // Debounced notes save
  useEffect(() => {
    if (notesDebounce) {
      clearTimeout(notesDebounce);
    }
    const timeout = setTimeout(() => {
      // Compare treating empty string and null/undefined as equivalent
      const currentNotes = notes || null;
      const meetingNotes = meeting.notes || null;
      if (currentNotes !== meetingNotes) {
        onUpdate({ notes: currentNotes });
      }
    }, 500);
    setNotesDebounce(timeout);

    return () => clearTimeout(timeout);
  }, [notes]);

  // Handle agenda item update
  const handleUpdateAgendaItem = useCallback((updatedItem) => {
    const updatedItems = agendaItems.map(item =>
      item.id === updatedItem.id ? updatedItem : item
    );
    setAgendaItems(updatedItems);
    onUpdate({ agendaItems: updatedItems, isCustomized: true });
  }, [agendaItems, onUpdate]);

  // Handle agenda item delete
  const handleDeleteAgendaItem = useCallback((itemId) => {
    const updatedItems = agendaItems.filter(item => item.id !== itemId);
    setAgendaItems(updatedItems);
    onUpdate({ agendaItems: updatedItems, isCustomized: true });
  }, [agendaItems, onUpdate]);

  // Add new agenda item
  const handleAddAgendaItem = useCallback(() => {
    const newItem = {
      id: generateUUID(),
      title: 'New Item',
      description: '',
      durationMinutes: 10,
      order: agendaItems.length,
      isCompleted: false,
      notes: '',
      linkedDocumentIds: [],
      linkedPolicyIds: []
    };
    const updatedItems = [...agendaItems, newItem];
    setAgendaItems(updatedItems);
    setExpandedAgendaItems(prev => new Set([...prev, newItem.id]));
    onUpdate({ agendaItems: updatedItems, isCustomized: true });
  }, [agendaItems, onUpdate]);

  // Toggle agenda item expand/collapse
  const handleToggleExpand = useCallback((itemId) => {
    setExpandedAgendaItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  // Drag and drop handlers
  const handleDragStart = useCallback((index) => {
    setDraggedIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (draggedIndex !== null && dragTargetIndex !== null && draggedIndex !== dragTargetIndex) {
      const newItems = [...agendaItems];
      const [moved] = newItems.splice(draggedIndex, 1);
      newItems.splice(dragTargetIndex, 0, moved);
      // Update order field
      const reordered = newItems.map((item, i) => ({ ...item, order: i }));
      setAgendaItems(reordered);
      onUpdate({ agendaItems: reordered, isCustomized: true });
    }
    setDraggedIndex(null);
    setDragTargetIndex(null);
  }, [draggedIndex, dragTargetIndex, agendaItems, onUpdate]);

  const handleDragOver = useCallback((index) => {
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragTargetIndex(index);
    }
  }, [draggedIndex]);

  // Add action item
  const handleAddActionItem = useCallback(() => {
    if (!newActionItem.trim()) return;

    const newItem = {
      id: generateUUID(),
      text: newActionItem.trim(),
      assignee: '',
      dueDate: null,
      isCompleted: false
    };

    const updatedItems = [...actionItems, newItem];
    setActionItems(updatedItems);
    setNewActionItem('');
    onUpdate({ actionItems: updatedItems });
  }, [newActionItem, actionItems, onUpdate]);

  // Toggle action item completion
  const handleToggleActionItem = useCallback((itemId) => {
    const updatedItems = actionItems.map(item =>
      item.id === itemId ? { ...item, isCompleted: !item.isCompleted } : item
    );
    setActionItems(updatedItems);
    onUpdate({ actionItems: updatedItems });
  }, [actionItems, onUpdate]);

  // Delete action item
  const handleDeleteActionItem = useCallback((itemId) => {
    const updatedItems = actionItems.filter(item => item.id !== itemId);
    setActionItems(updatedItems);
    onUpdate({ actionItems: updatedItems });
  }, [actionItems, onUpdate]);

  const isCompleted = meeting.status === 'completed';
  const isShared = meeting.isShared;
  const completedAgendaCount = agendaItems.filter(i => i.isCompleted).length;
  const completedActionCount = actionItems.filter(i => i.isCompleted).length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border bg-surface-card">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-text-primary truncate">
              {meeting.title}
            </h2>
            {meeting.seriesId && (
              <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-surface-hover rounded text-text-tertiary">
                <Repeat size={10} />
                Recurring
              </span>
            )}
            {isCompleted && (
              <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-success/10 rounded text-success">
                <CheckCircle size={10} />
                Completed
              </span>
            )}
            {isShared && (
              <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-500/10 rounded text-blue-400">
                Shared
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-text-secondary">
              {formatMeetingDate(meeting.date)}
            </span>
            <span className="flex items-center gap-1 text-xs text-text-tertiary">
              <Clock size={10} />
              {formatTime(meeting.startTime)} - {formatTime(meeting.endTime)}
            </span>
          </div>
          {series && (
            <p className="text-[10px] text-text-tertiary mt-1">
              Part of: {series.title}
            </p>
          )}
          {isShared && meeting.owner && (
            <p className="text-[10px] text-blue-400 mt-1">
              From: {meeting.owner}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isShared && !isCompleted && onEnterMeetingMode && (
            <button
              onClick={onEnterMeetingMode}
              className="flex items-center gap-1.5 px-3 py-1.5 mr-2 text-xs font-medium text-white bg-accent hover:bg-accent-bright rounded-lg transition-colors"
              title="Enter Meeting Mode"
            >
              <Monitor size={14} />
              Start Meeting
            </button>
          )}
          {!isShared && (
            <>
              <button
                onClick={onEdit}
                className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors"
                title="Edit meeting"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={onDelete}
                className="p-2 rounded-lg text-text-tertiary hover:text-red-500 hover:bg-red-50 transition-colors"
                title="Delete meeting"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Agenda Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ListChecks size={14} className="text-accent" />
              <h3 className="text-sm font-semibold text-text-primary">Agenda</h3>
              {agendaItems.length > 0 && (
                <span className="text-[10px] text-text-tertiary">
                  {completedAgendaCount}/{agendaItems.length}
                </span>
              )}
            </div>
            {!isShared && (
              <button
                onClick={handleAddAgendaItem}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent-bright transition-colors"
              >
                <Plus size={14} />
                Add Item
              </button>
            )}
          </div>

          {agendaItems.length === 0 ? (
            <div className="text-center py-6 bg-surface-elevated rounded-lg border border-dashed border-border">
              <ListChecks size={24} className="mx-auto mb-2 text-text-muted" />
              <p className="text-xs text-text-tertiary mb-2">
                No agenda items yet
              </p>
              {!isShared && (
                <button
                  onClick={handleAddAgendaItem}
                  className="text-xs text-accent hover:text-accent-bright transition-colors"
                >
                  + Add your first agenda item
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {agendaItems.map((item, index) => (
                <AgendaItem
                  key={item.id}
                  item={item}
                  index={index}
                  onUpdate={handleUpdateAgendaItem}
                  onDelete={handleDeleteAgendaItem}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  isDragging={draggedIndex === index}
                  isDragTarget={dragTargetIndex === index}
                  documents={documents}
                  policies={policies}
                  isExpanded={expandedAgendaItems.has(item.id)}
                  onToggleExpand={() => handleToggleExpand(item.id)}
                  readOnly={isShared}
                />
              ))}
            </div>
          )}
        </section>

        {/* Notes Section */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <FileText size={14} className="text-accent" />
            <h3 className="text-sm font-semibold text-text-primary">Meeting Notes</h3>
          </div>
          {isShared ? (
            notes ? (
              <div className="w-full min-h-[60px] p-3 text-sm bg-surface-card border border-border rounded-lg whitespace-pre-wrap">
                {notes}
              </div>
            ) : (
              <p className="text-xs text-text-muted italic py-3">No meeting notes.</p>
            )
          ) : (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Capture key discussion points, decisions, and takeaways..."
              className="w-full min-h-[120px] p-3 text-sm bg-surface-card border border-border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-accent"
            />
          )}
        </section>

        {/* Action Items Section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckSquare size={14} className="text-accent" />
              <h3 className="text-sm font-semibold text-text-primary">Action Items</h3>
              {actionItems.length > 0 && (
                <span className="text-[10px] text-text-tertiary">
                  {completedActionCount}/{actionItems.length}
                </span>
              )}
            </div>
          </div>

          {/* Action items list */}
          <div className="space-y-2 mb-3">
            {actionItems.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                  item.isCompleted
                    ? 'bg-surface-hover border-border opacity-60'
                    : 'bg-surface-card border-border'
                }`}
              >
                <button
                  onClick={() => !isShared && handleToggleActionItem(item.id)}
                  disabled={isShared}
                  className="flex-shrink-0"
                >
                  {item.isCompleted ? (
                    <CheckCircle size={16} className="text-success" />
                  ) : (
                    <Circle size={16} className="text-text-tertiary hover:text-accent" />
                  )}
                </button>
                <span className={`flex-1 text-sm ${item.isCompleted ? 'line-through text-text-tertiary' : 'text-text-primary'}`}>
                  {item.text}
                </span>
                {item.assignee && (
                  <span className="text-[10px] text-text-tertiary px-1.5 py-0.5 bg-surface-hover rounded">
                    {item.assignee}
                  </span>
                )}
                {!isShared && (
                  <button
                    onClick={() => handleDeleteActionItem(item.id)}
                    className="p-1 text-text-muted hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add action item */}
          {!isShared && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newActionItem}
                onChange={(e) => setNewActionItem(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddActionItem()}
                placeholder="Add an action item..."
                className="flex-1 text-sm px-3 py-2 bg-surface-card border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                onClick={handleAddActionItem}
                disabled={!newActionItem.trim()}
                className="px-3 py-2 text-sm font-medium text-white bg-accent hover:bg-accent-bright rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      {!isCompleted && !isShared && (
        <div className="p-4 border-t border-border bg-surface-card">
          <button
            onClick={onComplete}
            className="w-full py-2.5 text-sm font-medium text-white bg-success hover:bg-success/90 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Check size={16} />
            Mark Meeting Complete
          </button>
        </div>
      )}
    </div>
  );
}
